
var _o = new Map();

class Session {
  static _key(id, type) {
    return `${type}-${id}`;
  }

  static get(...keys) {
    var v = keys.map(key => _o.get(key));
    return v.length > 1 ? v : v[0];
  }

  static set(key, val) {
    _o.set(key, val);
    return Session;
  }

  static state(id, data = {}) {
    var key = Session._key(id, `state`);
    return _o.get(key) || _o.set(key, data).get(key);
  }

  static remove(key) {
    _o.delete(key);
    return Session;
  }
}

/**
 * Returns the active scope filter as a query-string fragment (e.g. "&repCode=ABC" or
 * "&custodianId=xyz") based on which of repCode / custodian is currently active in
 * session state.  Pass the result directly into a URL string.
 *
 * Rules:
 *   repCode set   → "&repCode=<encoded code>"   (empty string = null group)
 *   specific custodian selected → "&custodianId=<id>"
 *   "All Custodians" or neither → "" (no filter appended)
 */
export function getActiveScopeFilter(state: any): string {
  // Multi-select: emit comma-separated repCodes in single param
  if (state?.repCode?._multi && Array.isArray(state.repCode.codes)) {
    const codes = state.repCode.codes
      .map((r: any) => encodeURIComponent(r.code ?? 'UNASSIGNED'))
      .join(',');
    return `&repCodes=${codes}`;
  }

  if (state?.repCode) 
    return `&repCodes=${encodeURIComponent(state.repCode.code ?? 'UNASSIGNED')}`;
  
  if (state?.custodian?._id && state.custodian._id !== '*') 
    return `&custodianId=${state.custodian._id}`;
  
  return '';
}

/**
 * Returns a stable string cache-key for the currently active scope so that
 * accounts / contracts / invoices can be cached per-custodian or per-repCode.
 *
 * Cache Invalidation Note:
 * If a mutation occurs that shifts an item between scopes (e.g., changing an
 * account's Custodian via a wizard while viewing a RepCode), ensure the cached
 * subsets are invalidated. Component-level `_refresh()` gracefully dumps
 * `state._accountsCache[cacheKey]` for the *active* state.
 *
 * Rules:
 *   repCode active   → "repCode:<code>"  (UNASSIGNED suffix for no repCode)
 *   custodian active → custodian._id
 *   all / none       → "*"
 */
export function getActiveScopeKey(state: any): string {
  // Multi-select: use sorted code list as stable key
  if (state?.repCode?._multi && Array.isArray(state.repCode.codes)) {
    const codes = state.repCode.codes
      .map((r: any) => r.code ?? 'UNASSIGNED')
      .sort()
      .join(',');
    return `repCodes:${codes}`;
  }

  if (state?.repCode) 
    return `repCode:${state.repCode.code ?? 'UNASSIGNED'}`;
  
  return state?.custodian?._id ?? '*';
}

export default Session;
